import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import type {
  AuthState,
  ClerkOrganization,
  ClerkOrganizationMembership,
  ClerkUser,
} from '../../interfaces/auth.interface'

const initialState: AuthState = {
  isLoaded: false,
  isSignedIn: false,
  user: null,
  organization: null,
  memberships: [],
  sessionId: null,
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthLoaded: (state, action: PayloadAction<boolean>) => {
      state.isLoaded = action.payload
    },

    setUser: (state, action: PayloadAction<{ user: ClerkUser; sessionId: string }>) => {
      state.isLoaded = true
      state.isSignedIn = true
      state.user = action.payload.user
      state.sessionId = action.payload.sessionId
    },

    setOrganization: (state, action: PayloadAction<ClerkOrganization | null>) => {
      state.organization = action.payload
    },

    setMemberships: (state, action: PayloadAction<ClerkOrganizationMembership[]>) => {
      state.memberships = action.payload
    },

    syncOrganizationUpdate: (state, action: PayloadAction<ClerkOrganization>) => {
      state.organization = action.payload
      const membership = state.memberships.find((m) => m.organizationId === action.payload.id)
      if (membership) {
        membership.organizationName = action.payload.name
        membership.organizationSlug = action.payload.slug
        membership.organizationImageUrl = action.payload.imageUrl
      }
    },

    updateUser: (state, action: PayloadAction<Partial<ClerkUser>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
      }
    },

    clearAuth: (state) => {
      state.isLoaded = true
      state.isSignedIn = false
      state.user = null
      state.organization = null
      state.memberships = []
      state.sessionId = null
    },
  },
})

export const {
  setAuthLoaded,
  setUser,
  setOrganization,
  setMemberships,
  syncOrganizationUpdate,
  updateUser,
  clearAuth,
} = authSlice.actions

export const authReducer = authSlice.reducer

export const selectAuth = (state: { auth: AuthState }) => state.auth
export const selectUser = (state: { auth: AuthState }) => state.auth.user
export const selectOrganization = (state: { auth: AuthState }) => state.auth.organization
export const selectMemberships = (state: { auth: AuthState }) => state.auth.memberships
export const selectIsSignedIn = (state: { auth: AuthState }) => state.auth.isSignedIn
export const selectIsAuthLoaded = (state: { auth: AuthState }) => state.auth.isLoaded
export const selectSessionId = (state: { auth: AuthState }) => state.auth.sessionId
